<template>
    <section
        class="slice slice-testimonials slice-testimonials--columns"
        :class="{'slice-testimonials--columns--grey': backgroundColor === 'grey'}"
        aria-label="testimonials"
        data-testid="testimonials-columns-slice"
    >
        <div class="slice-content">
            <MktTitleBrand v-if="props.slice.primary.title" :field="props.slice.primary.title" data-testid="testimonials-columns-title" />
            <div v-if="props.slice.primary.testimonials">
                <TestimonialsCarousel :items="props.slice.primary.testimonials" :breakpoints="breakpoints" :spacebetween="40">
                    <template #before>
                        <img :src="quotesOpenSvg" width="50" class="slice-testimonials--columns__quotes-open" loading="lazy" />
                    </template>
                    <template #after>
                        <img :src="quotesCloseSvg" width="50" class="slice-testimonials--columns__quotes-close" loading="lazy" />
                    </template>
                    <template #card="{item, index}">
                        <TestimonialCard
                            :index="index"
                            :testimonial="item.description"
                            :name="item.name ?? ''"
                            :job-title="item.jobTitle ?? ''"
                            :image="item.image"
                        />
                    </template>
                </TestimonialsCarousel>
            </div>
        </div>
    </section>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {TestimonialsSlice} from '@prismicio-types';
    import {MktTitleBrand} from '#components';
    import quotesCloseSvg from '../../../../assets/svg/quotes-close.svg?url';
    import quotesOpenSvg from '../../../../assets/svg/quotes-open.svg?url';
    import TestimonialCard from '../components/TestimonialCard.vue';
    import TestimonialsCarousel, {type TestimonialsCarouselBreakpoints} from '../components/TestimonialsCarousel.vue';

    type TestimonialsColumnsInZone = Extract<TestimonialsSlice, {variation: 'testimonialsColumns'}>;

    const props = defineProps(getSliceComponentProps<TestimonialsColumnsInZone>());
    const backgroundColor = computed(() => props.slice.primary.backgroundColor);

    const breakpoints: TestimonialsCarouselBreakpoints = {
        0: {slidesPerView: 1.2},
        640: {slidesPerView: 1.2},
        840: {slidesPerView: 2.2},
        992: {slidesPerView: 3},
    };
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-testimonials--columns {
        --quotes-position-x: 50px;

        &--grey {
            background-color: var(--joy-color-neutral-20);
        }

        .testimonials-carousel__buttons {
            @include mq.screen_md(min) {
                margin-top: 0 !important;
            }
        }

        .testimonial-card {
            max-width: unset;
        }

        .title-brand {
            text-align: center;

            @include mq.screen_md(min) {
                margin-bottom: 0;
            }
        }

        .slice-content {
            position: relative;

            @include mq.screen_md(min) {
                .swiper-slide {
                    padding: 0 0 var(--joy-core-spacing-1) 0;
                }
            }
        }

        &__quotes-open,
        &__quotes-close {
            display: none;

            @include mq.screen_md(min) {
                position: relative;
                width: 50px;
                height: 50px;
                display: block;
            }
        }

        @include mq.screen_lg(min) {
            --quotes-position-x: 25px;
        }
        @include mq.screen_xlg(min) {
            --quotes-position-x: 0px;
        }

        &__quotes-open {
            left: calc(var(--quotes-position-x) - 50px);
            margin-bottom: var(--joy-core-spacing-5);
        }
        &__quotes-close {
            left: calc(100% - var(--quotes-position-x));
            margin-top: var(--joy-core-spacing-5);
        }
    }
</style>
